import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Copy, Check, TextSelect } from "lucide-react";

/**
 * Copy affordances for chat messages:
 *
 *  - MessageContextMenu — a right-click menu offering "Copy selection"
 *    (when text is highlighted) and "Copy message" (the full raw text).
 *  - SelectionCopyChip — a floating Copy pill that appears above any text
 *    the user highlights inside the messages container.
 *
 * Both are rendered through portals so they escape overflow/scroll clipping.
 */

export async function copyTextToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Clipboard API can be unavailable (non-secure context) — fall back to
    // the legacy execCommand path via an off-screen textarea.
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    document.body.appendChild(ta);
    ta.select();
    try {
      return document.execCommand("copy");
    } catch {
      return false;
    } finally {
      ta.remove();
    }
  }
}

export interface MessageCopyMenuState {
  x: number;
  y: number;
  /** Full raw text of the message under the cursor. */
  content: string;
  /** Text highlighted at the moment of right-click (may be empty). */
  selection: string;
}

export function MessageContextMenu({
  menu,
  onClose,
}: {
  menu: MessageCopyMenuState | null;
  onClose: () => void;
}) {
  const menuRef = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState<"selection" | "message" | null>(null);

  useEffect(() => {
    setCopied(null);
  }, [menu]);

  useEffect(() => {
    if (!menu) return;
    const close = () => onClose();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onMouseDown = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [menu, onClose]);

  if (!menu) return null;

  const handleCopy = async (text: string, which: "selection" | "message") => {
    const ok = await copyTextToClipboard(text);
    if (ok) {
      setCopied(which);
      setTimeout(onClose, 650);
    } else {
      onClose();
    }
  };

  const left = Math.min(menu.x, window.innerWidth - 210);
  const top = Math.min(menu.y, window.innerHeight - 96);
  const itemCls =
    "flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-primary hover:bg-surface-hover transition-colors";

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[100] min-w-[190px] rounded-xl border border-border bg-white py-1 shadow-lg dark:bg-surface"
      style={{ left, top }}
      role="menu"
    >
      {menu.selection.trim().length > 0 && (
        <button
          type="button"
          role="menuitem"
          className={itemCls}
          onClick={() => handleCopy(menu.selection, "selection")}
        >
          {copied === "selection" ? (
            <Check size={14} className="text-green-500" />
          ) : (
            <TextSelect size={14} />
          )}
          {copied === "selection" ? "Copied" : "Copy selection"}
        </button>
      )}
      <button
        type="button"
        role="menuitem"
        className={itemCls}
        onClick={() => handleCopy(menu.content, "message")}
      >
        {copied === "message" ? (
          <Check size={14} className="text-green-500" />
        ) : (
          <Copy size={14} />
        )}
        {copied === "message" ? "Copied" : "Copy message"}
      </button>
    </div>,
    document.body,
  );
}

/**
 * Floating "Copy" pill shown above highlighted text within the given
 * container. Appears on mouseup when a non-empty selection starts and ends
 * inside the container; hides on scroll, resize, or when the selection
 * collapses.
 */
export function SelectionCopyChip({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLElement | null>;
}) {
  const [pos, setPos] = useState<{ x: number; y: number; text: string } | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const update = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
        setPos(null);
        return;
      }
      const text = sel.toString();
      const container = containerRef.current;
      if (!text.trim() || !container) {
        setPos(null);
        return;
      }
      const anchorIn = sel.anchorNode && container.contains(sel.anchorNode);
      const focusIn = sel.focusNode && container.contains(sel.focusNode);
      if (!anchorIn || !focusIn) {
        setPos(null);
        return;
      }
      const rect = sel.getRangeAt(0).getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        setPos(null);
        return;
      }
      setPos({ x: rect.left + rect.width / 2, y: rect.top, text });
      setCopied(false);
    };
    // Defer so the selection has settled by the time we read it.
    const onMouseUp = () => setTimeout(update, 0);
    const onSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed) {
        setPos(null);
        setCopied(false);
      }
    };
    const hide = () => {
      setPos(null);
      setCopied(false);
    };
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("selectionchange", onSelectionChange);
    document.addEventListener("scroll", hide, true);
    window.addEventListener("resize", hide);
    return () => {
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("selectionchange", onSelectionChange);
      document.removeEventListener("scroll", hide, true);
      window.removeEventListener("resize", hide);
    };
  }, [containerRef]);

  if (!pos) return null;

  const top = Math.max(8, pos.y - 40);
  const left = Math.min(Math.max(pos.x, 48), window.innerWidth - 48);

  return createPortal(
    <button
      type="button"
      // Prevent the mousedown from collapsing the selection before click fires.
      onMouseDown={(e) => e.preventDefault()}
      onClick={async () => {
        const ok = await copyTextToClipboard(pos.text);
        if (ok) {
          setCopied(true);
          setTimeout(() => {
            setPos(null);
            setCopied(false);
            window.getSelection()?.removeAllRanges();
          }, 900);
        }
      }}
      className="fixed z-[100] inline-flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-border bg-white px-3 py-1.5 text-xs font-medium text-text-primary shadow-lg transition-colors hover:bg-surface-hover dark:bg-surface"
      style={{ left, top }}
      aria-label="Copy selected text"
    >
      {copied ? <Check size={12} className="text-green-500" /> : <Copy size={12} />}
      {copied ? "Copied" : "Copy"}
    </button>,
    document.body,
  );
}
